/**
 * CotizacionDetailSheet - Vista detallada de una cotización
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { TestTubes, ArrowRightLeft } from "lucide-react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

import { useApiError } from "@/hooks/use-api-error";
import { useCotizacion, useConvertirCotizacion } from "@/hooks/use-ordenes";

interface CotizacionDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cotizacionId: number;
  onConverted?: () => void;
}

function getEstadoBadgeVariant(
  estado: string
): "default" | "secondary" | "destructive" | "outline" {
  switch (estado) {
    case "VIGENTE":
      return "default";
    case "CONVERTIDA":
      return "secondary";
    case "VENCIDA":
      return "outline";
    case "CANCELADA":
      return "destructive";
    default:
      return "secondary";
  }
}

export function CotizacionDetailSheet({
  open,
  onOpenChange,
  cotizacionId,
  onConverted,
}: CotizacionDetailSheetProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const { data: cotizacion, isLoading } = useCotizacion(cotizacionId, { enabled: open });
  const [isConvertOpen, setIsConvertOpen] = useState(false);

  const convertirMutation = useConvertirCotizacion({
    onSuccess: () => {
      toast.success(t("ordenes.cotizaciones.toast.converted"));
      setIsConvertOpen(false);
      onConverted?.();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const handleConvert = () => {
    convertirMutation.mutate({
      id: cotizacionId,
      data: { paciente: cotizacion?.paciente },
    });
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-[540px] overflow-y-auto p-0">
          <SheetHeader className="px-4 sm:px-6 pt-6 pb-0">
            <SheetTitle>{t("ordenes.cotizaciones.title")}</SheetTitle>
            <SheetDescription>
              {isLoading ? <Skeleton className="h-4 w-48" /> : `# ${cotizacion?.numero}`}
            </SheetDescription>
          </SheetHeader>

          {isLoading ? (
            <div className="px-4 sm:px-6 py-4 space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : cotizacion ? (
            <div className="px-4 sm:px-6 pb-6 space-y-5">
              {/* Info general */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs">
                    {t("ordenes.cotizaciones.columns.patient")}
                  </p>
                  <p className="font-medium mt-0.5">
                    {cotizacion.paciente_nombre || t("common.notSpecified")}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">
                    {t("ordenes.cotizaciones.columns.status")}
                  </p>
                  <Badge variant={getEstadoBadgeVariant(cotizacion.estado)} className="mt-1">
                    {cotizacion.estado_display}
                  </Badge>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">
                    {t("ordenes.cotizaciones.validUntil")}
                  </p>
                  <p className="font-medium mt-0.5">
                    {new Date(cotizacion.fecha_vigencia).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">{t("common.createdAt")}</p>
                  <p className="font-medium mt-0.5">
                    {new Date(cotizacion.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">{t("ordenes.columns.branch")}</p>
                  <p className="font-medium mt-0.5">{cotizacion.sucursal_nombre}</p>
                </div>
                {cotizacion.orden_generada && (
                  <div>
                    <p className="text-muted-foreground text-xs">
                      {t("ordenes.cotizaciones.generatedOrder")}
                    </p>
                    <p className="font-medium mt-0.5">#{cotizacion.orden_generada}</p>
                  </div>
                )}
              </div>

              {cotizacion.observaciones && (
                <>
                  <Separator />
                  <div className="text-sm">
                    <p className="text-muted-foreground text-xs">
                      {t("ordenes.fields.observations")}
                    </p>
                    <p className="mt-1 text-muted-foreground/80 leading-relaxed">
                      {cotizacion.observaciones}
                    </p>
                  </div>
                </>
              )}

              <Separator />

              {/* Exámenes */}
              <div>
                <h3 className="text-sm font-medium mb-3">
                  {t("ordenes.examList")} ({cotizacion.total_examenes})
                </h3>
                <div className="space-y-1.5">
                  {cotizacion.examenes.map((ex) => (
                    <div key={ex.id} className="p-2.5 border rounded-lg text-sm bg-muted/30">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <TestTubes className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <span className="font-mono text-[11px] text-muted-foreground shrink-0">
                            {ex.examen_tipo_codigo}
                          </span>
                          <span className="truncate">{ex.examen_tipo_nombre}</span>
                        </div>
                        <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                          L {Number(ex.precio).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-3 pt-3 border-t text-sm font-semibold">
                  <span>Total</span>
                  <span className="tabular-nums">L {Number(cotizacion.total).toFixed(2)}</span>
                </div>
              </div>

              {/* Convert button */}
              {cotizacion.esta_vigente && cotizacion.is_active && (
                <>
                  <Separator />
                  <Button className="w-full" onClick={() => setIsConvertOpen(true)}>
                    <ArrowRightLeft className="mr-2 h-4 w-4" />
                    {t("ordenes.cotizaciones.convertToOrder")}
                  </Button>
                </>
              )}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={isConvertOpen}
        onOpenChange={setIsConvertOpen}
        title={t("ordenes.cotizaciones.confirmConvert")}
        description={t("ordenes.cotizaciones.confirmConvertDesc", { numero: cotizacion?.numero })}
        onConfirm={handleConvert}
      />
    </>
  );
}
